import { useMemo } from "react";
import { usePublicSettings } from "../api/queries";
import { usePrefs } from "../state/prefs";
import { resolveZone, zoneLabel, type ZoneMode } from "./format";
import { useNow } from "./useNow";

export interface ActiveZone {
  mode: ZoneMode;
  /** IANA zone name to hand to Intl. */
  name: string;
  /** The station's own zone, whatever mode is selected. */
  station: string;
  /** Abbreviation for right now — MDT in summer, MST in winter. */
  short: string;
}

/**
 * The abbreviation is the one part of this that depends on *when*, and it turns over at a
 * daylight-saving boundary. A minute is finer than that by a wide margin.
 */
const ZONE_TICK_MS = 60_000;

/**
 * The zone every rendered timestamp goes through.
 *
 * The station's zone comes from the server rather than being hardcoded: it is a runtime
 * setting, and the archive spans daylight-saving changes where a fixed offset would be
 * wrong for half the year.
 */
export function useZone(): ActiveZone {
  const mode = usePrefs((s) => s.zoneMode);
  const { data } = usePublicSettings();
  const station = data?.station_timezone ?? "America/Denver";
  const now = useNow(ZONE_TICK_MS);

  return useMemo(() => {
    const name = resolveZone({ mode, station });
    return { mode, name, station, short: zoneLabel(now, name) };
  }, [mode, station, now]);
}
